import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button, EmptyState, Modal, toast } from '@/components/ui'
import {
  useAddSampleRequestRep,
  useDeleteSampleRequestRep,
  useSampleRequestReps,
} from '../api/queries'
import type { SampleRequestRep } from '../api/types'

export function SampleRepsSection() {
  const { data, isLoading } = useSampleRequestReps()
  const add = useAddSampleRequestRep()
  const remove = useDeleteSampleRequestRep()

  const [pending, setPending] = useState<SampleRequestRep | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const reps = data?.reps ?? []

  function confirmRemove() {
    if (!pending) return
    const p = pending
    remove.mutate(p.id, {
      onSuccess: () => {
        toast.success(`Removed ${p.name}`)
        setPending(null)
      },
      onError: (err) =>
        toast.danger(`Remove failed${err instanceof Error ? `: ${err.message}` : ''}`),
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Sample Request Reps</h2>
          <p className="text-sm text-secondary">
            People who can be assigned to a Uviva sample request. Only the reps listed
            here appear in the &ldquo;Assigned rep&rdquo; dropdown on the request form. The
            assigned rep receives shipment + delivery notifications.
          </p>
        </div>
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          <Plus size={12} /> Add rep
        </Button>
      </header>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
      ) : reps.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No reps yet"
          description="Add the sales reps who should be assignable to sample requests."
        />
      ) : (
        <div className="overflow-hidden rounded-sm border border-border-subtle bg-surface">
          <ul className="flex flex-col divide-y divide-border-subtle">
            {reps.map((rep) => (
              <li key={rep.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="font-medium text-primary">{rep.name}</span>
                <span className="text-secondary">{rep.email}</span>
                {!rep.active ? <span className="text-role-metadata">inactive</span> : null}
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  className="ml-auto"
                  aria-label={`Remove ${rep.name}`}
                  onClick={() => setPending(rep)}
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending ? (
        <Modal
          open={pending !== null}
          onOpenChange={(open) => !open && setPending(null)}
          tone="danger"
          title="Remove rep"
          description={
            <>
              Remove <code>{pending.name}</code> ({pending.email}) from the assignable
              reps list?
            </>
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button variant="danger" loading={remove.isPending} onClick={confirmRemove}>
                Remove
              </Button>
            </>
          }
        />
      ) : null}

      <AddRepModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(name, email) =>
          add.mutate(
            { name, email },
            {
              onSuccess: () => {
                toast.success(`Added ${name}`)
                setAddOpen(false)
              },
              onError: (err) =>
                toast.danger(`Add failed${err instanceof Error ? `: ${err.message}` : ''}`),
            },
          )
        }
        submitting={add.isPending}
      />
    </section>
  )
}

function AddRepModal({
  open,
  onClose,
  onSubmit,
  submitting,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (name: string, email: string) => void
  submitting: boolean
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  function reset() {
    setName('')
    setEmail('')
  }
  if (!open) return null
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onClose()
        }
      }}
      tone="info"
      title="Add rep"
      description="The rep becomes selectable on the sample request form and receives shipment + delivery notifications for their requests."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!name.trim() || !email.trim()}
            onClick={() => onSubmit(name.trim(), email.trim())}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Rep name"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
            autoFocus
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="rep@siliconesplus.com"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
      </div>
    </Modal>
  )
}
